import clsx from "clsx";
import throttle from "lodash/throttle";
import React, { useEffect, useMemo, useState } from "react";
import { AppFooter } from "web/react/components/app-footer/app-footer";
import { FooterProps } from "web/react/components/footer/footer";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { ToastProvider } from "web/react/emo/toast/toast-provider";
import withReduxProvider from "web/react/redux-provider";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import PartnersNav from "./partners-nav";
import * as styles from "./partners-page.css";
import {
    PartnersAudience,
    PartnersBenefits,
    PartnersContact,
    PartnersHero,
    PartnersIntegration,
    PartnersModel,
    PartnersOnboarding,
    PartnersTrust,
} from "./sections";

interface PartnersPageProps {
    footer?: FooterProps;
}

function PartnersPage({ footer }: PartnersPageProps): JSX.Element {
    const [backToTopVisible, setBackToTopVisible] = useState(false);

    const handleScroll = useMemo(
        () =>
            throttle(() => {
                setBackToTopVisible(window.scrollY > window.innerHeight);
            }, 100),
        []
    );

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
            handleScroll.cancel();
        };
    }, [handleScroll]);

    function scrollToTop(): void {
        analytics.event({
            category: "back_to_top",
            action: "clicked",
            label: "back_to_top",
        });
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }

    return (
        <div className={styles.page}>
            <PartnersNav />
            <main>
                <PartnersHero />
                <PartnersModel />
                <PartnersBenefits />
                <PartnersAudience />
                <PartnersIntegration />
                <PartnersTrust />
                <PartnersOnboarding />
                <PartnersContact />
            </main>
            {footer && <AppFooter {...footer} />}
            <ToastProvider />
            <button
                className={clsx(styles.backToTop, backToTopVisible && styles.backToTopVisible)}
                onClick={scrollToTop}
                aria-label={gettext("back_to_top")}
            >
                <SVGIcon name="chevron" rotate={270} />
            </button>
        </div>
    );
}

export default withReduxProvider(PartnersPage);
